import {
  DataViewState,
  SearchFormState,
  TableColumn,
  UsePaginationReturn,
  UseTableStateReturn,
  useDataViewState,
  usePaginationState,
  useSearchState,
  useTableState,
} from '@vtex/admin-ui'
import { useCache } from 'hooks'
import {
  Dispatch,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react'
import { DEFAULT_SORT, ITEMS_PER_PAGE } from 'shared'
import type { DefaultProps, SimplifiedPromotion } from 'typings'

type Columns = Array<TableColumn<SimplifiedPromotion>>

type FilterFn = (item: SimplifiedPromotion) => boolean

export type FilterState<T = string> = {
  id: string
  values: T[]
  filterFn: FilterFn
  clear: () => void
}
interface PromotionsContext {
  paginationState: UsePaginationReturn
  viewState: DataViewState
  searchState: SearchFormState
  tableState: UseTableStateReturn<SimplifiedPromotion>
  filtersState: FilterState[]
  setItems: Dispatch<SetStateAction<SimplifiedPromotion[]>>
  setColumns: Dispatch<SetStateAction<Columns>>
  setFiltersState: Dispatch<SetStateAction<FilterState<string>[]>>
}

export const PromotionsContext = createContext<PromotionsContext | undefined>(
  undefined
)

export function PromotionsContextProvider(props: DefaultProps) {
  const [columns, setColumns] = useState<Columns>([])
  const [items, setItems] = useState<SimplifiedPromotion[]>([])
  const [filtersState, setFiltersState] = useState<FilterState[]>([])
  const { get, set } = useCache()

  const paginationState = usePaginationState({
    pageSize: ITEMS_PER_PAGE,
    initialPage: get('page'),
  })

  const viewState = useDataViewState({
    loading: true,
    empty: null,
    error: null,
    notFound: false,
  })

  const searchState = useSearchState({
    initialValue: get('searchState'),
  })

  const tableState = useTableState({
    columns,
    items,
    length: ITEMS_PER_PAGE,
    status: viewState.status,
    sort: {
      initialValue: get('tableSort', DEFAULT_SORT),
    },
  })

  const searchTerm = searchState.debouncedValue

  useEffect(() => {
    set('searchState', searchTerm)
  }, [searchTerm, set])

  const page = paginationState.currentPage

  useEffect(() => {
    set('page', page)
  }, [page, set])

  const { order, by } = tableState.sortState

  useEffect(() => {
    set('tableSort', {
      order,
      by,
    })
  }, [set, by, order])

  return (
    <PromotionsContext.Provider
      value={{
        paginationState,
        viewState,
        searchState,
        tableState,
        filtersState,
        setColumns,
        setItems,
        setFiltersState,
      }}
    >
      {props.children}
    </PromotionsContext.Provider>
  )
}
